// @ts-strict-ignore
import { BaseArticlesSequenceOperation } from './base-articles-sequence-operation';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '../../vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { WireBridgeService } from '@project/components/article/manager/wire-bridge.service';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';

export abstract class UndoBusbarArticleOperation extends BaseArticlesSequenceOperation {
  protected linkedOperation: UndoBusbarArticleOperation;
  protected _usageIdsToConnect: MetaElementUsageIdentityVO[] = [];

  protected constructor(busbarContainer: ArticleContainer,
                        projectModel: ProjectModel,
                        projectArticleService: ProjectArticleService,
                        projectMetaElementService: ProjectMetaElementService,
                        selectionModel: SelectionModel,
                        selectionModelService: SelectionModelService,
                        wireBridgeService: WireBridgeService,
                        articleContainerLoader: ArticleContainerLoader,
                        articleContainerFactory: ArticleContainerFactory,
                        containerService: ContainerService,
                        applicationRefreshService: ApplicationRefreshService,
                        detachArticleUsageService: DetachArticleUsageService,
                        private articleLineHolder: ArticleLineHolder,
                        private connectionInfoService: ConnectionInfoService) {
    super(
      [busbarContainer],
      projectModel,
      projectArticleService,
      projectMetaElementService,
      selectionModel,
      selectionModelService,
      wireBridgeService,
      articleContainerLoader,
      articleContainerFactory,
      containerService,
      applicationRefreshService,
      detachArticleUsageService);
  }

  get usageIdsToConnect(): MetaElementUsageIdentityVO[] {
    return this._usageIdsToConnect;
  }

  set usageIdsToConnect(usageIds: MetaElementUsageIdentityVO[]) {
    this._usageIdsToConnect = usageIds;
  }

  getBusbarUsageId(): string {
    return this.extractArticleUsageIds()[0];
  }

  getBusbarUsage(): ArticleUsageVO {
    return this.projectModel.getArticleUsage(this.getBusbarUsageId());
  }

  linkOperation(operation: UndoBusbarArticleOperation): void {
    this.linkedOperation = operation;
    operation.linkedOperation = this;
  }

  protected detachArticles(): Promise<void> {
    this.disconnectAccessories();
    return super.detachArticles();
  }

  protected async attachArticles(): Promise<void> {
    await super.attachArticles();
    this.connectAccessories();
  }

  private disconnectAccessories(): void {
    const articleUsage: ArticleUsageVO = this.getBusbarUsage();
    const connectedUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getForwardConnectedConnectionUsageIds(articleUsage);
    connectedUsageIds.forEach((connectedUsageId: MetaElementUsageIdentityVO) => {
      const connectedContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(connectedUsageId.articleUsageId);
      const connectedContainerConnection = connectedContainer.findConnection(connectedUsageId.metaElementName);
      ArticleUsageUtils.removeAssociationsWithUsage(articleUsage, connectedContainer.articleGUID);
      ArticleUsageUtils.removeAssociationsWithUsage(connectedContainer.articleUsage, articleUsage.id);
      connectedContainerConnection.metaElementUsage = null;
    });
    this.articleLineHolder.clearArticleLineCacheByGUIDs(connectedUsageIds.map((id) => id.articleUsageId));
    this.linkedOperation.usageIdsToConnect = connectedUsageIds;
  }

  private connectAccessories(): void {
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this.getBusbarUsageId());
    const acceptingArea: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(articleContainer.article.metaElements);
    const acceptingConnection: Connection = articleContainer.findConnection(acceptingArea.name);
    this.usageIdsToConnect.forEach((givingConnectionId: MetaElementUsageIdentityVO) => {
      const givingConnection: Connection = this.connectionInfoService.findConnectionByIdentity(givingConnectionId);
      this.projectMetaElementService.createUsageBetweenConnections(givingConnection, acceptingConnection);
    });
    this.usageIdsToConnect = [];
  }
}
